import { GatewayNodes } from '@flowx/designer/shared/entities/node.types'
import {
  centredOffset,
  fanOrder,
  rowRanks,
} from '@flowx/designer/shared/react-flow/auto-layout/row-ranks'

import { CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'

type Sequence = CanvasData['sequences'][number]

const isExclusive = (node: NodeDefinitionRender | undefined): boolean =>
  node?.type === GatewayNodes.GATEWAY_EXCLUSIVE
const isParallel = (node: NodeDefinitionRender | undefined): boolean =>
  node?.type === GatewayNodes.GATEWAY_PARALLEL

// Built once per sequence (node) list: every gateway lookup reads them, and one arrange makes
// hundreds of lookups. Canvas data is never mutated in place, so a list's index stays valid.
const outgoingIndex = new WeakMap<Sequence[], Map<number, Sequence[]>>()
const nodeIndex = new WeakMap<NodeDefinitionRender[], Map<number, NodeDefinitionRender>>()
const uuidIndex = new WeakMap<NodeDefinitionRender[], Map<string, number>>()

const cached = <K extends object, V>(index: WeakMap<K, V>, key: K, build: () => V): V => {
  if (!index.has(key)) {
    index.set(key, build())
  }

  return index.get(key)
}

/** Plain (non-boundary) outgoing sequences per node, in creation order. */
const outgoingByNode = (data: CanvasData): Map<number, Sequence[]> =>
  cached(outgoingIndex, data.sequences, () =>
    [...data.sequences]
      .filter((sequence) => !sequence.boundaryEventId)
      .sort((a, b) => a.id - b.id)
      .reduce(
        (outgoing, sequence) =>
          outgoing.set(sequence.startNodeId, [
            ...(outgoing.get(sequence.startNodeId) ?? []),
            sequence,
          ]),
        new Map<number, Sequence[]>()
      )
  )

const nodesByIdOf = (data: CanvasData): Map<number, NodeDefinitionRender> =>
  cached(nodeIndex, data.nodes, () => new Map(data.nodes.map((node) => [node.id, node])))

const idByUuidOf = (data: CanvasData): Map<string, number> =>
  cached(uuidIndex, data.nodes, () => new Map(data.nodes.map((node) => [node.flowxUuid, node.id])))

/**
 * An exclusive gateway's outgoing sequences in rule order — IF, ELSE IF…, ELSE, by `order` —
 * followed by any sequence no rule points at (a gateway not configured yet falls back to creation
 * order). The first one is the gateway's main branch.
 */
export function exclusiveBranchOrder(gateway: NodeDefinitionRender, data: CanvasData): Sequence[] {
  const outgoing = outgoingByNode(data).get(gateway.id) ?? []
  const idByUuid = idByUuidOf(data)
  const ruled = [...(gateway.nodeRule?.rules ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((rule) =>
      outgoing.find((sequence) => sequence.endNodeId === idByUuid.get(rule.nextNodeFlowxUuid))
    )
    .filter((sequence): sequence is Sequence => !!sequence)
    .filter((sequence, index, all) => all.indexOf(sequence) === index)

  return [...ruled, ...outgoing.filter((sequence) => !ruled.includes(sequence))]
}

/**
 * A parallel gateway's branches top to bottom, fanned symmetrically around its centre line:
 * the first branch in the middle, then alternately above and below.
 */
export function parallelBranchOrder(gateway: NodeDefinitionRender, data: CanvasData): Sequence[] {
  const outgoing = outgoingByNode(data).get(gateway.id) ?? []

  return fanOrder(outgoing)
}

/**
 * Where each branch of a gateway leaves it, as a vertical offset from the gateway's centre in
 * top-to-bottom order: an exclusive gateway's main branch exactly at the centre and its
 * alternatives below it; a parallel gateway's fan centred on it.
 */
export function gatewayBranchOffsets(
  gateway: NodeDefinitionRender,
  data: CanvasData
): { sequence: Sequence; offset: number }[] {
  if (isExclusive(gateway)) {
    return exclusiveBranchOrder(gateway, data).map((sequence, index) => ({
      sequence,
      offset: index,
    }))
  }
  if (isParallel(gateway)) {
    const fan = parallelBranchOrder(gateway, data)

    return fan.map((sequence, index) => ({ sequence, offset: centredOffset(index, fan.length) }))
  }

  return []
}

/**
 * The join of a parallel split: the nearest parallel gateway every branch reaches. Undefined for
 * a split whose branches never meet again (or meet only at an exclusive merge).
 */
export function parallelJoinOf(split: NodeDefinitionRender, data: CanvasData): number | undefined {
  const outgoing = outgoingByNode(data)
  const nodesById = nodesByIdOf(data)
  const distancesFrom = (startId: number): Map<number, number> => {
    const distances = new Map<number, number>([[startId, 0]])
    const queue = [startId]
    while (queue.length) {
      const current = queue.shift()
      ;(outgoing.get(current) ?? [])
        .map((sequence) => sequence.endNodeId)
        .filter((next) => next !== split.id && !distances.has(next))
        .forEach((next) => {
          distances.set(next, distances.get(current) + 1)
          queue.push(next)
        })
    }

    return distances
  }
  const branches = (outgoing.get(split.id) ?? []).map((sequence) =>
    distancesFrom(sequence.endNodeId)
  )
  if (branches.length < 2) {
    return undefined
  }
  const candidates = [...branches[0].keys()]
    .filter((id) => isParallel(nodesById.get(id)))
    .filter((id) => branches.every((reach) => reach.has(id)))
    .map((id) => ({ id, distance: Math.max(...branches.map((reach) => reach.get(id))) }))
    .sort((a, b) => a.distance - b.distance || a.id - b.id)

  return candidates[0]?.id
}

export interface MainPath {
  /** Sequences on the main path and along each parallel branch; they are kept straight. */
  sequenceIds: Set<number>
  /** Parallel split → join pairs with no middle branch between them; each is kept on one line. */
  parallelPairs: { splitId: number; joinId: number }[]
}

/**
 * The process's main path, walked from every start event: through an exclusive gateway along its
 * main branch (first rule), across a parallel block from its split straight to its join — each
 * branch on its own straight line, an odd fan's middle one on the split's — and through any other
 * node (or an unjoined split) along its first outgoing sequence. A loop ends the walk.
 */
export function mainPath(data: CanvasData, startIds: number[]): MainPath {
  const nodesById = nodesByIdOf(data)
  const outgoing = outgoingByNode(data)
  const visited = new Set<number>()
  const sequenceIds = new Set<number>()
  const parallelPairs: MainPath['parallelPairs'] = []

  /** Walks from `startId` up to `stopId`; `straightIntoStop` keeps the sequence into it too. */
  const walk = (startId: number, stopId?: number, straightIntoStop = true): void => {
    let current = nodesById.get(startId)
    while (current && current.id !== stopId && !visited.has(current.id)) {
      visited.add(current.id)
      const outs = outgoing.get(current.id) ?? []
      if (!outs.length) {
        break
      }
      const joinId =
        isParallel(current) && outs.length > 1 ? parallelJoinOf(current, data) : undefined
      if (joinId !== undefined) {
        // Every branch runs straight on its own line. An odd fan's middle branch is the split's
        // line and holds the join on it; an even fan has no middle branch, so the pair itself is
        // kept on one line.
        const [first, ...others] = outs
        if (outs.length % 2 === 1) {
          sequenceIds.add(first.id)
          walk(first.endNodeId, joinId)
        } else {
          parallelPairs.push({ splitId: current.id, joinId })
          walk(first.endNodeId, joinId, false)
        }
        others.forEach((branch) => walk(branch.endNodeId, joinId, false))
        current = nodesById.get(joinId)
      } else {
        // A split whose branches never join again carries on along its first branch
        const next = isExclusive(current) ? exclusiveBranchOrder(current, data)[0] : outs[0]
        if (straightIntoStop || next.endNodeId !== stopId) {
          sequenceIds.add(next.id)
        }
        current = nodesById.get(next.endNodeId)
      }
    }
  }

  startIds.forEach((startId) => walk(startId))

  return { sequenceIds, parallelPairs }
}

/**
 * How far above (negative) or below (positive) the main row each node reachable from a start
 * event belongs, in branch steps: the sum of the gateway branch offsets along its path — taking
 * the path that strays least, so a join lands on its split's line and a merge back on the main
 * row. Exception flows count as one step down, unless `boundaryStep` says otherwise.
 */
export function branchRanks(
  data: CanvasData,
  startIds: number[],
  boundaryStep: (sequence: Sequence) => number = () => 1
): Map<number, number> {
  const offsets = new Map(
    data.nodes.flatMap((node) =>
      gatewayBranchOffsets(node, data).map(({ sequence, offset }) => [sequence.id, offset] as const)
    )
  )
  const ownerOfBoundary = new Map(
    data.boundaryEvents.map((boundaryEvent) => [boundaryEvent.id, boundaryEvent.nodeDefinitionId])
  )
  const steps = data.sequences
    .filter(
      (sequence) => !sequence.boundaryEventId || ownerOfBoundary.has(sequence.boundaryEventId)
    )
    .map((sequence) => ({
      from: sequence.boundaryEventId
        ? ownerOfBoundary.get(sequence.boundaryEventId)
        : sequence.startNodeId,
      to: sequence.endNodeId,
      offset: sequence.boundaryEventId ? boundaryStep(sequence) : (offsets.get(sequence.id) ?? 0),
    }))

  return rowRanks(startIds, steps)
}
